import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getMessaging, Messaging, getToken, onMessage } from 'firebase/messaging';

// Firebase config for the firebase-peachmode project
// These are public client-side keys — safe to commit
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyA1uuI-1HtQhnHjnQGqtpKn3EQ0EHK1udY',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'fir-peachmode.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'firebase-peachmode',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'firebase-peachmode.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '429449494859',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '1:429449494859:web:8b8692f5b6f357ef2b28ce',
};

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

/**
 * Initialize Firebase app (idempotent)
 */
export function getFirebaseApp(): FirebaseApp | null {
  if (app) return app;

  // Don't initialize if critical config is missing
  if (!firebaseConfig.apiKey || !firebaseConfig.messagingSenderId) {
    console.warn('[Firebase] Missing config — push notifications disabled. Set REACT_APP_FIREBASE_* env vars.');
    return null;
  }

  try {
    app = getApps().length > 0 ? getApps()[0] : initializeApp(firebaseConfig);
    return app;
  } catch (err) {
    console.error('[Firebase] Init failed:', err);
    return null;
  }
}

/**
 * Get Firebase Messaging instance
 */
export function getFirebaseMessaging(): Messaging | null {
  if (messaging) return messaging;

  const firebaseApp = getFirebaseApp();
  if (!firebaseApp) return null;

  // Messaging requires service worker support
  if (!('serviceWorker' in navigator) || !('Notification' in window)) {
    console.warn('[Firebase] Service workers or Notifications not supported');
    return null;
  }

  try {
    messaging = getMessaging(firebaseApp);
    return messaging;
  } catch (err) {
    console.error('[Firebase] Messaging init failed:', err);
    return null;
  }
}

/**
 * Get the FCM registration token for this browser/device.
 * Requires notification permission to be granted.
 */
export async function getFcmToken(): Promise<string | null> {
  const msg = getFirebaseMessaging();
  if (!msg) return null;

  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY || 'BHZc5Qt2RcfhJjzX8uQ0T5TGQldz8M0yGukO9DHxfqcPbEMNBjjNhFtGeNf2lz7VJ6U501hjWL-rZYyO2bbGdTI';
  if (!vapidKey) {
    console.warn('[Firebase] REACT_APP_FIREBASE_VAPID_KEY not set — cannot get FCM token');
    return null;
  }

  try {
    // Get or create a service worker registration for Firebase
    let swRegistration = await navigator.serviceWorker.getRegistration('/firebase-cloud-messaging-push-scope');
    if (!swRegistration) {
      swRegistration = await navigator.serviceWorker.getRegistration();
    }

    const token = await getToken(msg, {
      vapidKey,
      serviceWorkerRegistration: swRegistration || undefined,
    });

    return token || null;
  } catch (err) {
    console.error('[Firebase] Failed to get FCM token:', err);
    return null;
  }
}

/**
 * Listen for foreground messages (when app is open and visible).
 * Returns an unsubscribe function.
 */
export function onForegroundMessage(callback: (payload: any) => void): (() => void) | null {
  const msg = getFirebaseMessaging();
  if (!msg) return null;

  return onMessage(msg, callback);
}
